import {
  LockIcon,
  SignInIcon,
  SignOutIcon,
  SpinnerIcon,
} from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { ReactNode } from "react";

/**
 * Lo que se ve cuando todavía no hay aplicación: entrando, o sin poder entrar.
 *
 * Van fuera de los dos armazones --no hay conmutador de modo que enseñar
 * porque no se está en ningún modo-- y centradas, como la pantalla de
 * arranque de antes.
 */
const SCREEN = "grid h-dvh place-items-center bg-background p-8";

/** Mientras se recupera la sesión o se va y vuelve del SSO. */
export function SessionPending({ children }: { readonly children: ReactNode }) {
  return (
    <div className={SCREEN} role="status">
      <p className="flex items-center gap-2 text-muted-foreground">
        <SpinnerIcon className="animate-spin" />
        {children}
      </p>
    </div>
  );
}

/**
 * No se pudo entrar, y se dice por qué.
 *
 * Siempre con una salida a mano: el SSO no se relanza solo desde aquí --eso
 * es justo lo que el anti-bucle impide--, pero la persona sí puede pedirlo.
 */
export function SessionProblem({
  title,
  reason,
  onSignIn,
  onSignOut,
}: {
  readonly title: string;
  readonly reason: ReactNode;
  readonly onSignIn?: () => void;
  readonly onSignOut?: () => void;
}) {
  return (
    <div className={SCREEN}>
      <Alert className="max-w-lg">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="grid gap-3">
          <div>{reason}</div>
          <div className="flex flex-wrap gap-2">
            {onSignIn !== undefined && (
              <Button size="sm" onClick={onSignIn}>
                <SignInIcon />
                Volver a entrar
              </Button>
            )}
            {onSignOut !== undefined && (
              <Button size="sm" variant="outline" onClick={onSignOut}>
                <SignOutIcon />
                Cerrar sesión
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * Hay sesión, pero no para esta aplicación: falta `embajadores:global.view`.
 *
 * Se dice CON QUÉ CUENTA se entró, porque lo más probable es que sea la
 * equivocada --el SSO de lala-ms es el mismo para todas sus aplicaciones--, y
 * se ofrece salir para entrar con otra.
 */
export function AccessDenied({
  email,
  onSignOut,
}: {
  readonly email: string;
  readonly onSignOut: () => void;
}) {
  return (
    <div className={SCREEN}>
      <Alert className="max-w-lg">
        <LockIcon />
        <AlertTitle>No tienes acceso a Embajadores</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>
            Has entrado como <strong>{email || "una cuenta sin correo"}</strong>
            , y esa cuenta no tiene permiso para usar esta aplicación. Si crees
            que debería, pídeselo a quien administra el programa; si no es tu
            cuenta de trabajo, sal y entra con otra.
          </p>
          <div>
            <Button size="sm" variant="outline" onClick={onSignOut}>
              <SignOutIcon />
              Cerrar sesión
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
